import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { BrandContent } from 'modules/Types';
import { getWebsitesByBrandId_api } from 'api/brandApi';
import { getOrganizationWebsites_api, OrganizationWebsiteData } from 'api/organizationApi';
import { getWebsites_api, WebsiteBuilderData } from 'api/websiteBuilderApi';

interface WebsitesState {
    /** Loading states */
    isLoadingLegacyWebsites: boolean;
    isLoadingWebsiteBuilder: boolean;
    isLegacyWebsitesInitialized: boolean;
    isWebsiteBuilderInitialized: boolean;
    hasWebsitesError: boolean;
    
    /** Legacy websites (presaves, old system) */
    legacyWebsites: BrandContent[];
    
    /** Website Builder sites (new system) */
    websiteBuilderSites: WebsiteBuilderData[];
    
    /** Organization-level websites */
    organizationWebsites: {
        legacyWebsites: any[];
        websiteBuilderSites: any[];
    };
    
    /** Methods */
    getLegacyWebsites: ({ brandId }: { brandId: number }) => Promise<BrandContent[]>;
    getWebsiteBuilderWebsites: ({ brandId, isAllWebsites, includeBrand }: { 
        brandId?: number, 
        isAllWebsites?: boolean, 
        includeBrand?: boolean 
    }) => Promise<WebsiteBuilderData[]>;
    getOrganizationWebsites: ({ organizationId }: { organizationId: number }) => Promise<{
        legacyWebsites: any[];
        websiteBuilderSites: any[];
    }>;
    setLegacyWebsites: (websites: BrandContent[]) => void;
    setWebsiteBuilderSites: (websites: WebsiteBuilderData[]) => void;
    resetWebsites: () => void;
}

const useWebsites = create<WebsitesState>()(
    immer((set, get) => ({
        // Initial state
        isLoadingLegacyWebsites: false,
        isLoadingWebsiteBuilder: false,
        isLegacyWebsitesInitialized: false,
        isWebsiteBuilderInitialized: false,
        hasWebsitesError: false,
        legacyWebsites: [],
        websiteBuilderSites: [],
        organizationWebsites: {
            legacyWebsites: [],
            websiteBuilderSites: []
        },
        
        // Methods
        /** Fetch legacy websites (presaves, old website system) */
        getLegacyWebsites: async ({ brandId }: { brandId: number }): Promise<BrandContent[]> => {
            set((state) => {
                state.isLoadingLegacyWebsites = true;
            });
            
            try {
                const data = await getWebsitesByBrandId_api({
                    brandId,
                    includeLinkedCampaigns: true
                });
                
                if (data) {
                    set((state) => {
                        state.legacyWebsites = data || [];
                        state.hasWebsitesError = false;
                    });
                    return data;
                }
                
                return [];
            } catch (error) {
                console.error('Error fetching legacy websites:', error);
                set((state) => {
                    state.hasWebsitesError = true;
                });
                return [];
            } finally {
                set((state) => {
                    state.isLoadingLegacyWebsites = false;
                    state.isLegacyWebsitesInitialized = true;
                });
            }
        },
        
        /** Fetch website builder sites (new website system) */
        getWebsiteBuilderWebsites: async ({
            brandId,
            isAllWebsites = false,
            includeBrand = false,
        }: {
            brandId?: number,
            isAllWebsites?: boolean,
            includeBrand?: boolean,
        }) => {
            set((state) => {
                state.isLoadingWebsiteBuilder = true;
            });
            
            try {
                const res = await getWebsites_api({ brandId, isAllWebsites, includeBrand });
                set((state) => {
                    state.websiteBuilderSites = res;
                    state.hasWebsitesError = false;
                });
                return res;
            } catch (error) {
                console.error('Error fetching website builder sites:', error);
                set((state) => {
                    state.hasWebsitesError = true;
                });
                return [];
            } finally {
                set((state) => {
                    state.isLoadingWebsiteBuilder = false;
                    state.isWebsiteBuilderInitialized = true;
                });
            }
        },
        
        getOrganizationWebsites: async ({ organizationId }: { organizationId: number }): Promise<{
            legacyWebsites: any[];
            websiteBuilderSites: any[];
        }> => {
            set((state) => {
                state.isLoadingLegacyWebsites = true;
                state.isLoadingWebsiteBuilder = true;
            });
            
            try {
                const data = await getOrganizationWebsites_api(organizationId, {
                    includeLinkedCampaigns: true
                });
                
                if (data) {
                    set((state) => {
                        state.organizationWebsites = {
                            legacyWebsites: data.legacyWebsites || [],
                            websiteBuilderSites: data.websiteBuilderSites || []
                        };
                        state.hasWebsitesError = false;
                    });
                    
                    return {
                        legacyWebsites: data.legacyWebsites || [],
                        websiteBuilderSites: data.websiteBuilderSites || []
                    };
                }
                
                return {
                    legacyWebsites: [],
                    websiteBuilderSites: []
                };
            } catch (error) {
                console.error('Error fetching organization websites:', error);
                set((state) => {
                    state.hasWebsitesError = true;
                });
                return {
                    legacyWebsites: [],
                    websiteBuilderSites: []
                };
            } finally {
                set((state) => {
                    state.isLoadingLegacyWebsites = false;
                    state.isLoadingWebsiteBuilder = false;
                    state.isLegacyWebsitesInitialized = true;
                    state.isWebsiteBuilderInitialized = true;
                });
            }
        },
        
        setLegacyWebsites: (websites: BrandContent[]) => {
            set((state) => {
                state.legacyWebsites = websites;
            });
        },
        
        setWebsiteBuilderSites: (websites: WebsiteBuilderData[]) => {
            set((state) => {
                state.websiteBuilderSites = websites;
            });
        },
        
        resetWebsites: () => {
            set((state) => {
                state.legacyWebsites = [];
                state.websiteBuilderSites = [];
                state.organizationWebsites = {
                    legacyWebsites: [],
                    websiteBuilderSites: []
                };
                state.isLegacyWebsitesInitialized = false;
                state.isWebsiteBuilderInitialized = false;
                state.hasWebsitesError = false;
            });
        }
    }))
);

export default useWebsites;